// AI Generated Component
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { SessionService } from '@monsido/core/session/session.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { DateTimeService } from 'app/services/date-time/date-time.service';
import { DomainService } from 'app/services/entities/domain/domain.service';
import { ChangesHistoryService } from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { CoreDomainService } from '../../../../core/services/domain/core-domain.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { PropertiesSumService } from 'app/filters/properties-sum.service';
import { PipeService } from '../../../../filters/pipe-transformation.service';
import { BaseChart } from '@client/app/modules/charts/models/chart';
import { StateService } from '@uirouter/core';
import { MON_MODULES } from '@monsido/core/constants/mon-modules.constant';
import { CHART_COLORS_CONSTANT } from '@client/app/constants/chart-colors';

@Component({
    selector: 'domain-dashboard-history',
    standalone: false,
    template: `
        <div class="card panel-monsido-card max-height d-flex flex-column justify-content-between">
            <mon-panel-header-next [pHeader]="'Scan History' | translate" [subHeader]="subHeaderText">
                <div class="text-right">
                    <span class="mr-6" *ngIf="historyData.length > 1 && historyData[1] > historyData[0]">
                        {{ historyData[1] - historyData[0]}} <span translate>page added since last scan</span>
                    </span>
                    <button class="btn btn-border-radius btn-secondary mon-disabled"
                        data-test="start-new-scan-button"
                        *ngIf="domainGroup === null && !hasCrawlStatus && !hasOnDemandScans"
                        ngbTooltip="{{ 'Your current plan does not include on-demand scans. Please reach out to your Acquia Web Governance representative to learn more about this option' | translate }}"
                        [triggers]="'focus hover'"
                        [placement]="'left'"
                        [container]="'body'"
                        aria-disabled="true">
                        <span class="fa fas fa-sync mr-1"></span>
                        <span translate>Start new scan</span>
                    </button>
                    <button class="btn btn-border-radius btn-secondary"
                        data-test="start-new-scan-button"
                        *ngIf="domainGroup === null && !hasCrawlStatus && hasOnDemandScans"
                        ngbTooltip="{{ 'The scan can take a few hours/days to finalise, depending on the size of the website' | translate }}"
                        [triggers]="'focus hover'"
                        [placement]="'left'"
                        [container]="'body'"
                        monModalConfirm="{{ 'The scan can take a few hours/days to finalise, depending on the size of the website.<br><br>Are you sure you want to start on-demand scan?' | translate}}"
                        (monModalConfirmAction)="onDemandScan()">
                        <span class="fa fas fa-sync mr-1"></span>
                        <span translate>Start new scan</span>
                    </button>
                    <button *ngIf="domainGroup === null && hasCrawlStatus"
                        data-test="ongoing-scan-info-button"
                        type="button"
                        class="text-nowrap btn btn-border-radius btn-secondary"
                        ngbTooltip="{{ 'Crawling' | translate }}"
                        [triggers]="'focus hover'"
                        [container]="'body'"
                        (click)="openScanDetails()">
                        <span class="text-dark-grey fas fa-spin fa-spinner outer-r-smurf"></span>
                        <span class="text-grey" translate>Scanning in progress</span>
                        <span class="text-dark-grey">
                            ({{ getDomainCrawlStatus() }} %)
                        </span>
                        <span class="text-link ml-2" translate>
                            Scan info
                        </span>
                    </button>
                </div>
            </mon-panel-header-next>
            <div class="card-body">
                <mon-spinner [loading]="loading">
                    <div class="px-9">
                        <mon-chart-barline
                            [labels]="labels"
                            [series]="series"
                            [override]="override"
                            [data]="data"
                            [options]="chart.options"
                            [colors]="colors"
                            [hidden]="loading"
                            [height]="235">
                        </mon-chart-barline>
                    </div>
                </mon-spinner>
            </div>
            <div class="card-footer text-right bg-white" *ngIf="showHistory">
                <a class="btn-link" [uiSref]="'base.customer.domain.changesHistory'" [uiParams]="historyCenterParameters" translate>Show history</a>
            </div>
        </div>
    `,
})
export class DomainDashboardHistoryComponent implements OnInit, OnDestroy {
    @Input() historyData: any = [];

    historyCenterParameters: any = {};
    subHeaderText: string = '';
    showHistory: boolean = false;
    hasOnDemandScans: boolean = false;
    hasCrawlStatus: boolean = false;
    domain: any = null;
    domainGroup: any = null;
    user: any = null;
    loading: boolean = true;
    pagesCrawled: number = 0;
    documentsCrawled: number = 0;
    chartDataUnits: any[] = [];

    // Chart properties
    series: any[] = [];
    labels: any[] = [];
    data: any[] = [];
    colors: any[] = [];
    override: any[] = [];
    options: any = {};
    chart: any;

    private subscriptions: any[] = [];

    constructor (
        private sessionService: SessionService,
        private translateService: TranslateService,
        private dateTimeService: DateTimeService,
        private domainService: DomainService,
        private changesHistoryService: ChangesHistoryService,
        private coreDomainService: CoreDomainService,
        private activeFeatureService: ActiveFeatureService,
        private propertiesSumService: PropertiesSumService,
        private pipeService: PipeService,
        private stateService: StateService,
    ) {}

    ngOnInit (): void {
        this.loading = true;
        this.historyCenterParameters = this.changesHistoryService.getHistoryCenterParameters('all');
        this.setupChart();
        this.showHistory = this.activeFeatureService.isFeatureActivePure('history_center_all');
        this.hasOnDemandScans = this.activeFeatureService.isFeatureActivePure('on_demand_scans');
        this.hasCrawlStatus = this.coreDomainService.hasCrawlStatus();
        this.domain = this.sessionService.domain;
        this.domainGroup = this.sessionService.domainGroup;
        this.user = this.sessionService.me;
        this.setupDomainHistory();
        this.setupSubHeaderText();

        const subscription = this.sessionService.domainObs.subscribe((domain: any) => {
            this.domain = domain;
            this.hasCrawlStatus = this.coreDomainService.hasCrawlStatus();
        });

        this.subscriptions.push(subscription);
    }

    ngOnDestroy (): void {
        for (const subscription of this.subscriptions ?? []) {
            subscription.unsubscribe();
        }
    }

    setupDomainHistory (): void {
        const pageCount: any[] = [];
        const documentCount: any[] = [];
        const seoCount: any[] = [];
        const policiesCount: any[] = [];
        const qaCount: any[] = [];
        const accessibilityCount: any[] = [];
        const totalCrawled: any[] = [];
        this.chartDataUnits = [];
        const showAcc = this.pipeService.get('hasAccess')(this.user, MON_MODULES.accessibility) && Boolean(this.domain.features.accessibility);
        const showPolicy = this.pipeService.get('hasAccess')(this.user, MON_MODULES.policies);
        const showSEO = this.pipeService.get('hasAccess')(this.user, MON_MODULES.seo);
        const showQA = this.pipeService.get('hasAccess')(this.user, MON_MODULES.qa);
        this.options.scales.xAxes[0].barPercentage = 0.1 * this.historyData.length;
        this.pagesCrawled = this.historyData.length > 0 ? this.historyData[0].page_count : 0;
        this.documentsCrawled = this.historyData.length > 0 ? this.historyData[0].documents_count : 0;

        this.historyData.reverse().forEach((scan: any) => {
            this.labels.push(this.dateTimeService.format(scan.queued_at || scan.date, 'MMM DD'));
            pageCount.push(scan.page_count || 0);
            seoCount.push(scan.pages_with_seo_errors_count || 0);
            policiesCount.push(scan.pages_with_policy_matches_count || 0);
            qaCount.push(scan.pages_with_notifications_count || 0);
            accessibilityCount.push(scan.pages_with_accessibility_errors_count || 0);
            documentCount.push(scan.documents_count || 0);
            totalCrawled.push(this.propertiesSumService.sum(scan, ['documents_count', 'page_count']));
        });

        if (showPolicy || this.hasData(policiesCount)) {
            this.series.push(this.translateService.getString('Content Policies'));
            this.data.push(policiesCount);
            this.chartDataUnits.push(this.translateService.getString('pages'));
            this.colors.push(CHART_COLORS_CONSTANT.CERISE);
            this.override.push({
                label: this.translateService.getString('Content Policies'),
                borderWidth: 1,
                type: 'bar',
                yAxisID: 'y-axis-0',
                xAxisID: 'x-axis-0',
                backgroundColor: CHART_COLORS_CONSTANT.CERISE,
                borderColor: CHART_COLORS_CONSTANT.CERISE,
                borderSkipped: ['top', 'bottom'],
                pointRadius: 3,
            });
        }

        if (showQA || this.hasData(qaCount)) {
            this.series.push(this.translateService.getString('QA'));
            this.data.push(qaCount);
            this.chartDataUnits.push(this.translateService.getString('pages'));
            this.colors.push(CHART_COLORS_CONSTANT.LIGHT_BLUE);
            this.override.push({
                label: this.translateService.getString('QA'),
                borderWidth: 1,
                type: 'bar',
                yAxisID: 'y-axis-0',
                xAxisID: 'x-axis-0',
                backgroundColor: CHART_COLORS_CONSTANT.LIGHT_BLUE,
                borderColor: CHART_COLORS_CONSTANT.LIGHT_BLUE,
                borderSkipped: ['top', 'bottom'],
                pointRadius: 3,
            });
        }

        if (showAcc || this.hasData(accessibilityCount)) {
            this.series.push(this.translateService.getString('Accessibility'));
            this.data.push(accessibilityCount);
            this.chartDataUnits.push(this.translateService.getString('pages'));
            this.colors.push(CHART_COLORS_CONSTANT.LIGHT_PURPLE);
            this.override.push({
                label: 'Accessibility',
                borderWidth: 1,
                type: 'bar',
                yAxisID: 'y-axis-0',
                xAxisID: 'x-axis-0',
                backgroundColor: CHART_COLORS_CONSTANT.LIGHT_PURPLE,
                borderColor: CHART_COLORS_CONSTANT.LIGHT_PURPLE,
                borderSkipped: ['top', 'bottom'],
                pointRadius: 3,
            });
        }

        if (showSEO || this.hasData(seoCount)) {
            this.series.push(this.translateService.getString('SEO Issues'));
            this.data.push(seoCount);
            this.chartDataUnits.push(this.translateService.getString('pages'));
            this.colors.push(CHART_COLORS_CONSTANT.LIGHT_YELLOW);
            this.override.push({
                label: this.translateService.getString('SEO Issues'),
                borderWidth: 1,
                type: 'bar',
                yAxisID: 'y-axis-0',
                xAxisID: 'x-axis-0',
                backgroundColor: CHART_COLORS_CONSTANT.LIGHT_YELLOW,
                borderColor: CHART_COLORS_CONSTANT.LIGHT_YELLOW,
                borderSkipped: ['top', 'bottom'],
                pointRadius: 3,
            });
        }

        this.series.push(this.translateService.getString('Pages crawled'));
        this.colors.push(CHART_COLORS_CONSTANT.TURQUOISE);
        this.data.push(pageCount);
        this.chartDataUnits.push(this.translateService.getString('pages'));
        this.override.push({
            label: this.translateService.getString('Pages crawled'),
            borderWidth: 2,
            type: 'line',
            fill: false,
            yAxisID: 'y-axis-0',
            xAxisID: 'x-axis-0',
            backgroundColor: CHART_COLORS_CONSTANT.TURQUOISE,
            borderColor: CHART_COLORS_CONSTANT.TURQUOISE,
            borderSkipped: ['top', 'bottom'],
            pointRadius: 3,
        });

        if (this.domain.scan.scan_documents || this.hasData(documentCount)) {
            this.data.push(documentCount);
            this.data.push(totalCrawled);
            this.chartDataUnits.push(this.translateService.getString('documents'), this.translateService.getString('pages'));
            this.series.push(this.translateService.getString('Documents crawled'), this.translateService.getString('Total crawled'));
            this.colors.push(CHART_COLORS_CONSTANT.GREEN_BLUE, CHART_COLORS_CONSTANT.LIGHT_TURQUOISE);
            this.override.push(
                {
                    label: this.translateService.getString('Documents crawled'),
                    borderWidth: 2,
                    type: 'line',
                    fill: false,
                    yAxisID: 'y-axis-0',
                    xAxisID: 'x-axis-0',
                    borderColor: CHART_COLORS_CONSTANT.GREEN_BLUE,
                    backgroundColor: CHART_COLORS_CONSTANT.GREEN_BLUE,
                    borderSkipped: ['top', 'bottom'],
                    pointRadius: 3,
                },
                {
                    label: this.translateService.getString('Total crawled'),
                    borderWidth: 2,
                    type: 'line',
                    fill: false,
                    yAxisID: 'y-axis-0',
                    xAxisID: 'x-axis-0',
                    borderColor: CHART_COLORS_CONSTANT.LIGHT_TURQUOISE,
                    backgroundColor: CHART_COLORS_CONSTANT.LIGHT_TURQUOISE,
                    borderSkipped: ['top', 'bottom'],
                    pointRadius: 3,
                },
            );
        }

        this.loading = false;
    }

    hasData (arr: any[]): boolean {
        const sum = Array.isArray(arr) && arr.reduce((acc: number, curr: any) => {
            if (!Number.isNaN(curr)) {
                acc += curr;
            }
            return acc;
        }, 0);
        const n = Number(sum);
        return !Number.isNaN(n) && n > 0;
    }

    setupChart (): void {
        this.series = [];
        this.labels = [];
        this.data = [];
        this.colors = [];
        this.override = [];

        this.options = {
            tooltips: {
                mode: 'label',
                intersect: false,
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        return (
                            data.datasets[tooltipItem.datasetIndex].label +
                            ': ' +
                            this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index])
                        );
                    },
                },
            },
            scales: {
                xAxes: [
                    {
                        id: 'x-axis-0',
                        barPercentage: 1,
                        categoryPercentage: 0.5,
                        type: 'category',
                        gridLines: {
                            offsetGridLines: true,
                            display: false,
                        },
                    },
                ],
                yAxes: [
                    {
                        id: 'y-axis-0',
                        ticks: {
                            min: 0,
                            beginAtZero: true,
                            maxTicksLimit: 4,
                            callback: (value: any) => {
                                return this.pipeService.get('monNumber')(value, '0,0a').toUpperCase();
                            },
                        },
                    },
                ],
            },
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    boxWidth: 13,
                },
            },
        };
        this.chart = new BaseChart(this.options);
        this.chart.setTooltipCustom({
            mode: 'label',
            intersect: false,
            callbacks: {
                label: (tooltipItem: any, data: any) => {
                    return (
                        data.datasets[tooltipItem.datasetIndex].label +
                        ': ' +
                        this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index], '0,0') +
                        ' ' +
                        this.chartDataUnits[tooltipItem.datasetIndex]
                    );
                },
            },
        });
        this.chart.setScalesSettings({
            xAxes: [
                {
                    id: 'x-axis-0',
                    barPercentage: 1,
                    categoryPercentage: 0.5,
                    type: 'category',
                    gridLines: {
                        offsetGridLines: true,
                        display: false,
                    },
                },
            ],
            yAxes: [
                {
                    id: 'y-axis-0',
                    ticks: {
                        min: 0,
                        beginAtZero: true,
                        maxTicksLimit: 4,
                        callback: (value: any) => {
                            return this.pipeService.get('monNumber')(value, '0,0a').toUpperCase();
                        },
                    },
                },
            ],
        });
    }

    setupSubHeaderText (): void {
        this.subHeaderText =
            this.translateService.getString('Last scan') +
            ' ' +
            this.pipeService.get('monNumber')(this.pagesCrawled) +
            ' ' +
            this.pipeService.get('translateWithPlural')('page', this.pagesCrawled, 'pages');
        if (this.domain.scan.scan_documents) {
            this.subHeaderText +=
                ' ' +
                this.translateService.getString('and') +
                ' ' +
                this.pipeService.get('monNumber')(this.documentsCrawled) +
                ' ' +
                this.pipeService.get('translateWithPlural')('doc', this.documentsCrawled, 'docs');
        }
    }

    onDemandScan (): void {
        const domainId = this.domain.id || this.sessionService.getDomainId();
        this.domainService.rescan(domainId).then(() => {
            if (this.sessionService.domain !== null && domainId === this.sessionService.domain.id) {
                this.sessionService.domain.crawl_status = { progress: 0 } as any;
                this.domain = this.sessionService.domain;
            }
            this.hasCrawlStatus = this.coreDomainService.hasCrawlStatus();
        }, () => {});
    }

    openScanDetails (): void {
        this.coreDomainService.openScanDetails();
    }

    getDomainCrawlStatus (): any {
        return this.coreDomainService.getDomainCrawlStatus();
    }
}
